import React from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Collapse,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/system";
import {
  SettingsOutlined,
  ChevronLeft,
  HomeOutlined,
  ReceiptLongOutlined,
  CloudDownloadOutlined,
  Backup,
  Logout,
  ManageAccounts,
  ExpandLess,
  ExpandMore,
  InsertChartOutlined,
  Terminal,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import logo from "../images/bsnl.png";

const Sidebar = ({
  user,
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const [openSubmenu, setOpenSubmenu] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();
  const [userDetails, setUserDetails] = useState(null);

  useEffect(() => {
    const storedUserData = sessionStorage.getItem("userDetails");
    if (storedUserData) {
      const parsedData = JSON.parse(storedUserData);
      setUserDetails(parsedData);
    }
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("userData");
    sessionStorage.removeItem("lastVisitedPage");
    navigate("/");
  };
  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);
  function getRoleName(roleId) {
    const roleMapping = {
      1: "Operator",
      2: "Configuration Manager",
      3: "Admin",
      4: "Super Admin",
    };
    return roleMapping[roleId] || "Unknown Role";
  }
  const navItems = [
    {
      text: "Dashboard",
      icon: <HomeOutlined />,
      path: "/dashboard",
    },
    {
      text: "Configuration",
      icon: <SettingsOutlined />,
      path: "/configuration",
    },
    {
      text: "OTA Upgrade",
      icon: <CloudDownloadOutlined />,
      path: "/ota_upgrade",
    },
    {
      text: "Logs",
      icon: <ReceiptLongOutlined />,
      path: "/logs",
      submenu: [
        {
          text: "Data Logs",
          icon: <InsertChartOutlined />,
          path: "/logs/data_logs",
        },
        // Check user role for "System Logs"
        userDetails && (userDetails.role === 3 || userDetails.role === 4)
          ? {
              text: "System Logs",
              icon: <Terminal />,
              path: "/logs/system_logs",
            }
          : null,
      ].filter((item) => item !== null), // Remove null items from submenu
    },
    // Check user role for "User Management" and include it in the top-level navItems
    userDetails && (userDetails.role === 3 || userDetails.role === 4)
      ? {
          text: "User Management",
          icon: <ManageAccounts />,
          path: "/user_management",
        }
      : null,
    {
      text: "Backup",
      icon: <Backup />,
      path: "/backup",
    },
  ].filter((item) => item !== null); // Remove null items from the top-level navItems

  const handleSubmenuToggle = (itemText) => {
    setOpenSubmenu((prev) => (prev === itemText ? "" : itemText));
  };
  const CardContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    margin: "1.5rem 2rem 0 3rem",
  }));

  const TextContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  }));

  const EmailText = styled(Typography)(({ theme }) => ({
    fontSize: "10px",
    wordBreak: "break-word",
    color: "black",
    fontWeight: "medium",
  }));

  const RoleText = styled(Typography)(({ theme }) => ({
    fontSize: "8px",
    color: theme.palette.text.secondary,
  }));
  return (
    <Box component="nav">
      {isSidebarOpen && (
        <Drawer
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant="persistent"
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              color: theme.palette.secondary[200],
              backgroundColor: theme.palette.background.alt,
              boxSizing: "border-box",
              borderWidth: isNonMobile ? 0 : "2px",
              width: drawerWidth,
            },
          }}
        >
          <Box width="100%">
            <Box m="1.5rem 2rem 2rem 3rem">
              <FlexBetween color={theme.palette.secondary.main}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap="0.5rem"
                  justifyContent="center"
                  width="100%"
                >
                  {/* Replace the Typography with an image */}
                  <img
                    src={logo}
                    alt="Logo"
                    style={{
                      width: "70px",
                      height: "auto",
                      objectFit: "contain",
                      borderRadius: "10px",
                    }}
                  />
                </Box>
                {!isNonMobile && (
                  <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    <ChevronLeft />
                  </IconButton>
                )}
              </FlexBetween>
            </Box>

            <List>
              {navItems.map(({ text, icon, path, submenu }) => {
                if (!icon) {
                  return (
                    <Typography key={text} sx={{ m: "2.25rem 0 1rem 3rem" }}>
                      {text}
                    </Typography>
                  );
                }

                const isSubmenuOpen = openSubmenu === text;

                return (
                  <Box key={text}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() =>
                          submenu ? handleSubmenuToggle(text) : navigate(path)
                        }
                        sx={{
                          backgroundColor:
                            active === path
                              ? theme.palette.secondary[300]
                              : "transparent",
                          color:
                            active === path
                              ? theme.palette.primary[600]
                              : theme.palette.secondary[100],
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            ml: "2rem",
                            color:
                              active === path
                                ? theme.palette.primary[600]
                                : theme.palette.secondary[200],
                          }}
                        >
                          {icon}
                        </ListItemIcon>
                        <ListItemText primary={text} />
                        {submenu ? (
                          isSubmenuOpen ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )
                        ) : null}
                      </ListItemButton>
                    </ListItem>
                    {submenu && (
                      <Collapse in={isSubmenuOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {submenu.map(
                            ({
                              text: subText,
                              icon: subIcon,
                              path: subPath,
                            }) => (
                              <ListItemButton
                                key={subText}
                                onClick={() => {
                                  navigate(subPath);
                                  setActive(subPath);
                                }}
                                sx={{
                                  pl: "4rem",
                                  backgroundColor:
                                    active === subPath
                                      ? theme.palette.secondary[300]
                                      : "transparent",
                                  color:
                                    active === subPath
                                      ? theme.palette.primary[600]
                                      : theme.palette.secondary[100],
                                }}
                              >
                                <ListItemIcon
                                  sx={{
                                    color:
                                      active === subPath
                                        ? theme.palette.primary[600]
                                        : theme.palette.secondary[200],
                                  }}
                                >
                                  {subIcon}
                                </ListItemIcon>
                                <ListItemText primary={subText} />
                              </ListItemButton>
                            )
                          )}
                        </List>
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </List>
          </Box>

          <Box position="absolute" bottom="2rem">
            <CardContainer>
              <Avatar
                backgroundColor="#997d3d"
                sx={{ width: 24, height: 24 }}
              />
              <TextContainer>
                {userDetails ? (
                  <>
                    <EmailText>{userDetails.email}</EmailText>
                    <RoleText>{getRoleName(userDetails.role)}</RoleText>
                  </>
                ) : (
                  <Typography variant="body2">Loading...</Typography>
                )}
              </TextContainer>
            </CardContainer>
            <FlexBetween
              textTransform="none"
              gap="1rem"
              m="1.5rem 2rem 0 5.2rem"
            >
              <Logout
                onClick={handleLogout}
                sx={{
                  color: theme.palette.secondary[300],
                  fontSize: "25px ",
                }}
              />
            </FlexBetween>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
